import React, { useState, useEffect, useRef } from "react"
import styled, { css } from "styled-components"
import ResizeObserver from "resize-observer-polyfill"
import { useStaticQuery, graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-react-intl"
import { useLocation } from "@reach/router"
import { useSpring, animated } from "react-spring"
import useMeasure from "react-use-measure"
import { useKeyPressEvent, useMedia } from "react-use"

import Button from "./Button"
import Snippet from "./Snippet"
import StyledLink from "./Link"

import { media } from "../styles"
import {
  getOpenNavigationSection,
  isBrowser,
  isNavigationItemActive,
} from "../helpers"
import NavToggleSVG from "../images/icons/nav-toggle.inline.svg"
import NavCloseSVG from "../images/icons/nav-close.inline.svg"
import HeaderSearch from "./HeaderSearch"

const NavContainer = styled.div`
  position: relative;
  z-index: 99999;

  @media ${media.lg} {
    display: none;
  }

  @media print {
    display: none;
  }
`

const Toggle = styled.button`
  align-items: center;
  appearance: none;
  background: transparent;
  border: none;
  color: var(--color-spaceblue);
  cursor: pointer;
  display: flex;
  height: 30px;
  justify-content: center;
  margin: 0;
  padding: 0;
  width: 30px;
`

const Flyout = styled(animated.nav)`
  background: var(--color-grey-light);
  bottom: 0;
  box-shadow: -1px 0 0 0 rgba(var(--color-neutral-dark-rgb), 0.7);
  max-width: 95%;
  overflow: auto;
  padding: 80px 0 40px;
  position: fixed;
  right: -1px;
  top: 0;
  width: 470px;
  pointer-events: ${({ $isOpen }) => ($isOpen ? "auto" : "none")};
  visibility: ${({ $isOpen }) => ($isOpen ? "visible" : "hidden")};

  .button {
    margin-top: 20px;
  }

  @media ${media.md} {
    padding: 150px 0 40px 0;
  }
`

const Close = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  color: var(--color-spaceblue);
  cursor: pointer;
  margin: 0;
  padding: 0;
  position: absolute;
  right: 10%;
  top: 20px;
`

const Menu = styled.ul`
  font-size: var(--font-size-22);
  font-weight: 400;
  letter-spacing: 0.6px;
  list-style: none;
  margin: 0 0 30px;
  padding: 0;
  width: 95%; // menu is 5% out of viewport

  ${(props) =>
    props.$isSubmenu &&
    css`
      padding-left: 30px;
    `}

  a {
    align-items: center;
    color: inherit;
    display: inline-flex;
    text-decoration: none;

    svg {
      flex: none;
    }

    svg:first-child {
      margin-right: 11px;
    }

    .link-icon {
      height: auto;
      margin: 0 0 0 8px !important;
      width: 9px;
    }

    &:hover,
    &:focus {
      color: var(--color-neutral-dark);
    }
  }
`

const MenuItem = styled.li`
  border-bottom: 1px solid var(--color-stone);
  margin: 0;
  padding: 16px 20px;

  &:last-child {
    border: none;
  }

  ${Menu} ${Menu} & {
    font-size: var(--font-size-16);
    border: none;
    line-height: 1.25;
    margin: 0;
    padding: 5px 0;
  }
`

const MenuHeader = styled.h3`
  font-size: var(--font-size-18);
  font-weight: 700;
  margin: 5px 0 20px;
  padding: 0;
`

const Link = styled(StyledLink)`
  ul ul & {
    ${(props) =>
      props.$active &&
      css`
        color: var(--color-neutral-dark);
        font-weight: 700;
      `}
  }
`

const SubmenuToggle = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  color: var(--color-spaceblue);
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  padding: 0;
  text-transform: inherit;
  width: 100%;

  &:hover,
  &:focus {
    color: var(--color-neutral-dark);
  }

  ${(props) =>
    props.$isOpen &&
    css`
      ${SubmenuToggleIcon} {
        transform: rotate(180deg);
      }
    `}
`

const SubmenuToggleIcon = styled.span`
  align-items: center;
  display: flex;
  margin: 0 0 0 auto;
  transition: transform var(--transition-time) ease-in-out;

  svg path {
    transition: opacity var(--transition-time) ease-in-out;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const Search = styled(HeaderSearch)`
  .search-label {
    margin-left: -2px;
  }
`

const ButtonRow = styled.div`
  padding: 0 20px;
`

const SubNavContent = styled(animated.div)`
  margin: 0 -6px;
  padding: 0 6px;
  width: calc(100% + 12px);

  @media ${media.lg} {
    padding: 0;
  }
`

const SubNav = ({ id, isOpen, mode, children }) => {
  const [menuHeight, setMenuHeight] = useState("0px")
  const [ref, { height }] = useMeasure({ polyfill: ResizeObserver })
  const prefersReducedMotion = useMedia("(prefers-reduced-motion: reduce)")

  const expand = useSpring({
    config: {
      friction: 30,
      tension: 300,
    },
    height: isOpen ? `${menuHeight + 40}px` : "0px",
    opacity: isOpen ? 1 : 0,
    overflow: "hidden",
    paddingBottom: isOpen ? "19px" : "0px",
    paddingTop: isOpen ? "19px" : "0px",
    immediate: prefersReducedMotion,
  })

  useEffect(() => {
    //Sets initial height
    setMenuHeight(height)
  }, [height, isOpen])

  return (
    <SubNavContent id={id} style={expand} inert={!isOpen ? "" : undefined}>
      <div ref={ref}>
        {mode === "grid" ? <>{children}</> : <Menu>{children}</Menu>}
      </div>
    </SubNavContent>
  )
}

const HeaderNavigationMobile = (props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [subOpen, setSubOpen] = useState()
  const intl = useIntl()
  const { pathname } = useLocation()
  let body, bodyClassList
  const flyoutRef = useRef()
  const toggleRef = useRef()
  const menuItemRefs = useRef({})
  const prefersReducedMotion = useMedia("(prefers-reduced-motion: reduce)")

  const flyoutSpring = useSpring({
    config: {
      friction: 30,
      tension: 300,
    },
    transform: isOpen ? `translate3d(5%, 0, 0)` : `translate3d(100%, 0, 0)`,
    immediate: prefersReducedMotion,
  })

  if (isBrowser()) {
    body = document.querySelector("body")
    bodyClassList = body.classList
  }

  useEffect(() => {
    bodyClassList.remove("scroll-locked")
  }, [bodyClassList])

  useKeyPressEvent("Escape", () => {
    if (isOpen) {
      if (subOpen) {
        // Find the parent button for the currently open submenu
        const openIndex = navItems?.findIndex((item) => item.slug === subOpen)
        if (openIndex !== -1 && menuItemRefs.current[openIndex]?.current) {
          // Return focus to the parent button
          menuItemRefs.current[openIndex].current.focus()
        }
        toggleSubMenu({ path: null })
      } else {
        toggle()
      }
    }
  })

  const toggle = () => {
    const willBeOpen = !isOpen
    setIsOpen(willBeOpen)
    bodyClassList.toggle("scroll-locked")

    // Only close submenus when closing the main menu, not when opening it
    if (!willBeOpen) {
      toggleSubMenu({ path: null })
    }

    toggleRef.current.focus()
  }

  const toggleSubMenu = ({ path }) => {
    setSubOpen(path || null)
  }

  const toggleSubMenuWithFocus = ({ path, buttonRef }) => {
    setSubOpen(path || null)
    // Ensure focus remains on the button after the state change
    // This prevents VoiceOver from jumping to the beginning of the page
    if (buttonRef?.current) {
      // Use a small delay to ensure the DOM has updated
      setTimeout(() => {
        buttonRef.current.focus()
      }, 10)
    }
  }

  const data = useStaticQuery(graphql`
    query HeaderNavigationMobileQuery {
      allNavigationJson {
        nodes {
          locale
          items {
            label
            slug
            mobileOnly
            # url
            mode
            items {
              label
              slug
              url
            }
          }
        }
      }
      allProductCollections: allContentfulProductCollection(
        filter: { visible: { eq: true }, isOld: { ne: true } }
        sort: { sortOrder: ASC }
      ) {
        nodes {
          id
          node_locale
          icon: iconSmall {
            ...ContentfulImageData
          }
          name
          items: products {
            ... on ContentfulProduct {
              name
              slug
            }
          }
        }
      }
      allIndustryCollections: allContentfulIndustryCollection(
        sort: { sortOrder: ASC }
      ) {
        nodes {
          id
          name
          description: navigationDescription
          node_locale
          items: industries {
            name
            slug
            hasIndustryPage
            hideIndustryPageFromNavigation
          }
        }
      }
    }
  `)

  const navItems = data.allNavigationJson.nodes.find(
    (node) => node.locale === intl.locale
  )?.items

  // Set the correct navigation section as open based on current path
  useEffect(() => {
    if (navItems) {
      setSubOpen(getOpenNavigationSection(pathname, navItems))
    }
  }, [pathname, navItems])

  const productCollections = data.allProductCollections.nodes.filter(
    (node) => node.node_locale === intl.locale
  )

  const industryCollections = data.allIndustryCollections.nodes.filter(
    (node) => node.node_locale === intl.locale
  )

  const renderMenuItem = ({ index, item, prefix, subOpen, parent }) => {
    // Create ref for this menu item if it doesn't exist
    if (!menuItemRefs.current[index]) {
      menuItemRefs.current[index] = React.createRef()
    }

    if (item.items?.length > 0) {
      return (
        <SubmenuToggle
          aria-controls={`mobile-submenu-${index}`}
          aria-haspopup="true"
          id={`mobile-menu-item-${index}`}
          $isOpen={subOpen === item.slug}
          onClick={(e) => {
            e.preventDefault()
            toggleSubMenuWithFocus({
              path: subOpen === item.slug ? null : item.slug,
              buttonRef: menuItemRefs.current[index],
            })
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault()
              toggleSubMenuWithFocus({
                path: subOpen === item.slug ? null : item.slug,
                buttonRef: menuItemRefs.current[index],
              })
            }
          }}
          type="button"
          ref={menuItemRefs.current[index]}
        >
          {item.label}
          <SubmenuToggleIcon aria-hidden="true">
            <svg
              width="19"
              height="19"
              viewBox="0 0 19 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.50138 18.0004V0.999595"
                stroke="#1D222F"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={subOpen === item.slug ? 0 : 1}
              />
              <path
                d="M1.00155 9.49845L18.0024 9.49845"
                stroke="#1D222F"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </SubmenuToggleIcon>
        </SubmenuToggle>
      )
    } else if (item.slug) {
      const slug = prefix ? `${prefix}/${item.slug}/` : item.slug
      return (
        <Link
          arrow={!!parent}
          $active={isNavigationItemActive(pathname, slug)}
          intl={item.intl === false}
          to={slug}
        >
          {item.label}
        </Link>
      )
    } else if (item.url) {
      return (
        <Link arrow={!!parent} intl={item.intl === false} to={item.url}>
          {item.label}
        </Link>
      )
    } else {
      return item.label
    }
  }

  const renderCollection = ({ collection, label, parent, prefix }) => (
    <div key={parent} aria-label={label}>
      {collection.map((group) => (
        <React.Fragment key={group.id}>
          <MenuHeader>{group.name}</MenuHeader>
          <Menu $isSubmenu>
            {group.items.map((item) => (
              <MenuItem key={item.slug}>
                {renderMenuItem({
                  item: {
                    label: item.name,
                    slug: item.slug,
                  },
                  parent: parent,
                  prefix: prefix,
                })}
              </MenuItem>
            ))}
          </Menu>
        </React.Fragment>
      ))}
    </div>
  )

  return (
    <NavContainer
      {...props}
      aria-label={intl.formatMessage({
        id: "accessibility.regions.mainNavigation",
      })}
      role="region"
    >
      <Toggle
        aria-label={intl.formatMessage({
          id: "accessibility.regions.mainNavigation",
        })}
        id="mobile-menu"
        onClick={() => toggle()}
        ref={toggleRef}
      >
        <NavToggleSVG aria-hidden="true" />
      </Toggle>
      <Flyout
        aria-labelledby={`mobile-menu`}
        aria-hidden={!isOpen}
        role="navigation"
        style={flyoutSpring}
        ref={flyoutRef}
        $isOpen={isOpen}
        inert={!isOpen ? "" : undefined}
      >
        <Close
          onClick={() => toggle()}
          aria-label={intl.formatMessage({
            id: "close",
          })}
        >
          <NavCloseSVG aria-hidden="true" />
        </Close>

        <Menu>
          {navItems.map((navItem, topIndex) => (
            <MenuItem key={navItem.label}>
              {renderMenuItem({
                item: navItem,
                subOpen,
                index: topIndex,
              })}
              {navItem.items && (
                <SubNav
                  id={`mobile-submenu-${topIndex}`}
                  index={topIndex}
                  isOpen={subOpen === navItem.slug}
                  mode={navItem.mode}
                >
                  {navItem.items.map((subItem) => {
                    switch (subItem.label) {
                      case "RENDER_SERVICES":
                        return renderCollection({
                          collection: productCollections,
                          label: navItem.label,
                          parent: navItem.slug,
                          prefix: `/${intl.locale}/services`,
                        })
                      case "RENDER_INDUSTRIES":
                        return renderCollection({
                          collection: industryCollections,
                          label: navItem.label,
                          parent: navItem.slug,
                          prefix: `/${intl.locale}/industries`,
                        })
                      default:
                        return (
                          <MenuItem key={subItem.label}>
                            {renderMenuItem({
                              item: subItem,
                              subOpen,
                              parent: navItem.slug,
                            })}
                          </MenuItem>
                        )
                    }
                  })}
                </SubNav>
              )}
            </MenuItem>
          ))}
          <MenuItem>
            <Search
              mode="link"
              label={intl.formatMessage({
                id: "header.shortlinks.search",
              })}
            />
          </MenuItem>
        </Menu>
        <ButtonRow>
          <Button
            arrow
            as={Link}
            intl={false}
            to={Snippet({ id: "navigationCTA", field: "linkUrl" })}
          >
            <Snippet id="navigationCTA" field="linkText" />
          </Button>
        </ButtonRow>
      </Flyout>
    </NavContainer>
  )
}

export default HeaderNavigationMobile
